import React, { useCallback, useEffect, useState } from "react";
import blackOval from "../../../assets/images/map_v2/black-ovall.svg";
import blackOval2 from "../../../assets/images/map_v2/black-ovall-2.svg";
import { AnimatePresence } from "framer-motion";
import PassPopup from "./components/PassPopup";
import useSessionId from "../events/useSessionId";
import useDeviceId from "../events/useDeviceId";
import withEventsLogger from "../events/withEventsLogger";
import SidebarContainer from "./components/SidebarContainer";
import MobileBrandInfo from "./components/MobileBrandInfo";
import MapContainer from "./components/MapContainer";
import { useMapSetup } from "./hooks/useMapSetup";
import { useLocationManager } from "./hooks/useLocationManager";
import { debounce } from "./utils/debounce";
import { useTagsFilter } from "./hooks/useTagsFilter";
import { useFetchLocationsAndBrands } from "./hooks/useFetchLocationsAndBrands";
import { useSyncedState } from "./utils/useSyncedState";
import { useEnterpriseOptions } from "./hooks/useEnterpriseOptions";
import { useMapCentering } from "./hooks/useMapCentering";
import { useMapHandlers } from "./hooks/useMapHandlers";
import {
  LocationsAndBrandsData,
  StaticLocation,
  Tier,
  UserLocation,
  Location,
  BrandList,
  LatLngLike,
  SelectedFilters,
  CustomMarker,
} from "./types/mapData";
import ZoomBox from "./components/ZoomBox";
import MapHeaderContainer from "./components/MapHeaderContainer";
import { getPacContainerClass } from "./utils/colorModeClass";
import useIsMobile from "../../hooks/useIsMobile";
import ToggleSwitch from "./components/ToggleSwitch";
import { filterLocationsByBrandAndDelivery } from "./utils/hydrateLocationsAndBrands";

type AppProps = {
  tiers: Array<Tier>;
  cloudinaryPath: string;
  widget: boolean;
  enterprise: string;
  totalLocationCount: number;
  brandSlug: string;
  showSearchBox: boolean;
  showSidebar: boolean;
  showZoombox: boolean;
  isHamburgerRequired: boolean;
  showPassPopup: boolean;
  logEvents: (events: Array<any>) => Promise<void>;
};

const memoizedOval = (props: AppProps) => {
  return require(
    `../../../assets/images/enterprise/${props.enterprise}/map_v2/oval.svg`,
  );
};

const memoizedLogo = (props: AppProps) => {
  return require(
    `../../../assets/images/enterprise/${props.enterprise}/icons/logo.png`,
  );
};

const App = (props: AppProps) => {
  // ─────────────────────────────────────────
  // Derived Config & Assets
  // ─────────────────────────────────────────
  const {
    tiers,
    cloudinaryPath,
    widget,
    enterprise,
    totalLocationCount,
    logEvents,
    brandSlug = "",
    showSearchBox = true,
    showSidebar = true,
    showZoombox = true,
    isHamburgerRequired = true,
    showPassPopup = true,
  } = props;
  const oval = memoizedOval(props);
  const logo = memoizedLogo(props);
  let counter = 0;
  const deviceId = useDeviceId();
  const sessionId = useSessionId();
  const queryParams = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );
  const {
    colorMode,
    showDeliveryOnly,
    purchaseModeTiers,
    isPassPage,
    ctaDisplay,
    ikname,
    inKindDomain,
    newCloudinaryPath,
  } = useEnterpriseOptions(enterprise, queryParams, cloudinaryPath);

  // ─────────────────────────────────────────
  // React State Variables
  // ─────────────────────────────────────────
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [staticLocation, setStaticLocation] = useState<StaticLocation | null>(
    null,
  );
  const [initLocations, setInitLocations] = useState<Location[]>([]);
  const [brands, setBrands] = useState<BrandList | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [curBrand, setCurBrand] = useState(0);
  const [curLoc, setCurLoc] = useState<Location | null>(null);
  const [markersState, setMarkersState] = useState<google.maps.Marker[]>([]);
  const [enteredThroughButton, setEnteredThroughButton] = useState(false);
  const [popUpOpen, setPopUpOpen] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [userLocErr, setUserLocErr] = useState(false);
  const [didRun, setDidRun] = useState(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [tags, setTags] = useState(null);
  const [viewCenter, setViewCenter] = useState<LatLngLike | null>(null);
  const loadingDetails = false;
  const [selectedCuisines, setSelectedCuisines] =
    useState<SelectedFilters | null>(null);
  const [selectedMoods, setSelectedMoods] = useState<SelectedFilters | null>(
    null,
  );
  const [cuisinesIsOpen, setCuisinesIsOpen] = useState(false);
  const [moodsIsOpen, setMoodsIsOpen] = useState(false);
  const [initSelectedTag, setInitSelectedTag] =
    useState<SelectedFilters | null>(null);
  const [curWidth, setCurWidth] = useState(window.innerWidth >= 800);
  const [placeHolder, setPlaceHolder] = useState(
    window.innerWidth <= 600 ? "Search..." : "Search city or venue...",
  );
  const [locations, setLocations, locationsRef] = useSyncedState([]);
  const [listState, setListState, listStateRef] = useSyncedState(1);
  const [curMarker, setCurMarker, curMarkerRef] = useSyncedState(null);
  const [hamburgerState, setHamburgerState, hamburgerStateRef] =
    useSyncedState(false);
  const [infoOpenMobile, setInfoOpenMobile] = useSyncedState(false);
  const [filteredLocations, setFilteredLocations, filteredLocationsRef] =
    useSyncedState([]);
  const [showBonusBox, setShowBonusBox, showBonusBoxRef] = useSyncedState(true);
  const reRenderList = React.useRef(false);
  const isMobile = useIsMobile(800);
  // ─────────────────────────────────────────
  // Analytics
  // ─────────────────────────────────────────
  const handleAmplitude = async (eventName: string, eventProperties?: any) => {
    await logEvents([
      {
        session_id: sessionId,
        event_name: eventName,
        device_id: deviceId,
        event_properties: {
          ...eventProperties,
        },
      },
    ]);
  };
  const handleMapLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  // ─────────────────────────────────────────
  // Lifecycle Effects
  // ─────────────────────────────────────────

  useEffect(() => {
    handleStart();
  }, []);

  useEffect(() => {
    handleListStateChange();
  }, [listState]);

  useEffect(() => {
    handleInitSelected();
  }, [tags]);

  useEffect(() => {
    handleTagsChange();
  }, [selectedCuisines, selectedMoods]);

  useEffect(() => {
    handleBrandChange();
  }, [curBrand]);

  useEffect(() => {
    if (showSearchBox) handleOpenModal();
  }, [infoOpenMobile]);

  //backup user location functionality
  useEffect(() => {
    handleUserLocationChange();
  }, [userLocation]);

  useEffect(() => {
    if (!!map && curWidth === true) {
      changeCenterOnResize({
        latlng: map.getCenter() as google.maps.LatLng,
        type: "larger",
      });
    } else if (!!map) {
      changeCenterOnResize({
        latlng: map.getCenter() as google.maps.LatLng,
        type: "smaller",
      });
    }
  }, [curWidth]);

  useEffect(() => {
    if (!map) return;
    handleMapChange();
    setCenter(map.getCenter());
  }, [map, brandSlug]);

  //build search functionality
  useEffect(() => {
    handleMarkersStateChange();
  }, [markersState]);

  //change UI to show selected marker as black
  useEffect(() => {
    handleMarkerChange();
  }, [curMarker]);

  // ─────────────────────────────────────────
  // Map Interaction and Marker Handling
  // ─────────────────────────────────────────

  const handleMapChange = async () => {
    if (!!userLocation && !!map) {
      await fetchLocationsAndBrands();
    }

    window.addEventListener("resize", handleHeight);

    if (showSearchBox) {
      const infoContainer = document.querySelector(".info-container-inner");
      if (infoContainer) {
        infoContainer.addEventListener("scroll", () => {
          if (listStateRef.current === 2 && ctaDisplay !== "none") {
            handleScrollShow();
          }
        });
      }
    }

    if (!!map) {
      map.addListener("tilesloaded", () => {
        const toAddClass = document.getElementsByClassName("pac-container")[0];
        if (!!toAddClass) {
          toAddClass?.classList.add(getPacContainerClass(widget, colorMode));
        }
      });

      map.addListener(
        "dragend",
        debounce(() => {
          handleAmplitude("click_web_map_content");
          reRenderList.current = true;
          setViewCenter(null);
        }, 300),
      );
      map.addListener("zoom_changed", () => {
        reRenderList.current = true;
      });
      map.addListener("idle", () => {
        if (!reRenderList.current) return;
        const center = map.getCenter();
        setUserLocation({
          isSearch: false,
          isRecenter: true,
          isLoc: false,
          location: [center.lat(), center.lng()],
        });
        reRenderList.current = false;
      });
    } else {
      handleHeight();
    }
  };
  const handleMarkerChange = () => {
    if (curMarker) {
      curMarker.setZIndex(1000);
      curMarker.setIcon({
        url: !!curMarker.isMulti ? blackOval : blackOval2,
        labelOrigin: new google.maps.Point(15.5, 14),
      });
    }
  };

  const handleMarkersStateChange = () => {
    if (markersState?.length > 0 && didRun === false) {
      setDidRun(true);
      if (showSearchBox) initAutoComplete();
    }
  };

  // ─────────────────────────────────────────
  // User Location Handling
  // ─────────────────────────────────────────

  const handleUserLocationChange = () => {
    if (!!userLocation && !userLocation.isSearch && !userLocation.isRecenter) {
      if (!userLocation.location || userLocation?.location?.length === 0) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location: UserLocation = {
              location: [position.coords.latitude, position.coords.longitude],
            };
            setUserLocation(location);
            setStaticLocation(location);
          },
          (error) => {
            const location: UserLocation = {
              location: [40.73, -74],
            };
            setUserLocation(location);
            setStaticLocation(location);
            setUserLocErr(true);
          },
        );
      } else if (!userLocation.isSearch) {
        handleSetMap();
      }
    } else {
      handleMoveCenter();
    }
    setCurrentPage(1);
    handleCurMarker(null);
  };
  // ─────────────────────────────────────────
  // Filter / State Logic
  // ─────────────────────────────────────────
  const handleScrollShow = () => {
    const lineToUse = !!purchaseModeTiers
      ? document.getElementsByClassName("map-tier")[1]?.getBoundingClientRect()
          .bottom
      : document
          .getElementsByClassName("map-subscribe-and-earn")[0]
          ?.getBoundingClientRect().bottom;
    const infoContainerInner = document.querySelector(
      ".info-container-inner",
    ) as HTMLDivElement;
    const isBelowLine =
      infoContainerInner?.getBoundingClientRect().bottom - 60 > lineToUse;
    if (!!isBelowLine && showBonusBoxRef.current === true) {
      setShowBonusBox(false);
    } else if (!isBelowLine && showBonusBoxRef.current === false) {
      setShowBonusBox(true);
    }
  };
  const handleListStateChange = () => {
    let container =
      window.innerWidth <= 800
        ? document.getElementsByClassName("map-outside")[0]
        : document.getElementsByClassName("info-container-inner")[0];
    if (!!container) {
      container.scrollTop = 0;
    }
  };
  const handleBrandChange = () => {
    const container = document.getElementsByClassName(
      "detafils-container",
    )[0] as HTMLElement | undefined;
    if (container) {
      container.style.opacity = "0";
      setTimeout(() => {
        container.style.transition = "opacity .5s";
        container.style.opacity = "1";
        setTimeout(() => {
          container.style.transition = "";
        }, 500);
      }, 100);
    }
  };

  // ─────────────────────────────────────────
  // UI Utility Handlers
  // ─────────────────────────────────────────

  const handleOpenModal = () => {
    const infoContainer = document.getElementsByClassName(
      "info-container",
    )[0] as HTMLElement | undefined;
    if (!infoContainer) return;

    if (!infoOpenMobile) {
      setEnteredThroughButton(false);
      infoContainer.classList.remove("visible");
      setTimeout(() => {
        infoContainer.style.display = "none";
      }, 500);
    } else {
      infoContainer.style.display = "block";
      setTimeout(() => {
        infoContainer.classList.add("visible");
      }, 50);
    }
  };

  const handleCloseMenus = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest(".menu-items")) {
      return;
    }
    if (moodsIsOpen) setMoodsIsOpen(false);
    if (cuisinesIsOpen) setCuisinesIsOpen(false);
  };

  const handleHeight = debounce(() => {
    const innerHeight = String(window.innerHeight) + "px";
    (document.getElementsByClassName("outer")[0] as HTMLElement).style.height =
      innerHeight;
    if (window.innerWidth <= 600) {
      setPlaceHolder("Search...");
    } else {
      setPlaceHolder("Search city or venue...");
    }
    setCurWidth(window.innerWidth >= 800);
  }, 300);

  // ─────────────────────────────────────────
  // Search and Centering
  // ─────────────────────────────────────────
  // Local type for enriched marker structure

  const initAutoComplete = () => {
    // Create the search box and link it to the UI element.
    const input = document.getElementById(
      "pac-input",
    ) as HTMLInputElement | null;
    if (!input || !map) return;

    // @ts-ignore
    const searchBox = new google.maps.places.SearchBox(input);

    // Bias the SearchBox results towards current map's viewport.
    map.addListener("bounds_changed", () => {
      const bounds = map.getBounds();
      if (!bounds) return;
      searchBox.setBounds(
        new google.maps.LatLngBounds(
          bounds.getSouthWest(),
          bounds.getNorthEast(),
        ),
      );
    });

    input.addEventListener("keyup", (event) => {
      let custom = document.getElementsByClassName("pac-custom");
      while (custom.length > 0) {
        custom[0].remove();
      }
      let markerPlaces: CustomMarker[] = [];
      if (input.value.length > 2) {
        markerPlaces = (markersState as CustomMarker[]).filter(
          (marker) =>
            marker.locName &&
            marker.locName
              .toUpperCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .includes(input.value.toUpperCase().trim()),
        );
        const lengthToUse = markerPlaces?.length > 5 ? 5 : markerPlaces.length;
        const toAppend = document.getElementsByClassName("pac-container")[0];
        toAppend.classList.add(getPacContainerClass(widget, colorMode));

        for (let i = 0; i < lengthToUse; i++) {
          const brand = brands?.[markerPlaces[i].brand_id];

          if (!brand) continue;
          const heroImage = brand.branding.hero_image.value ?? "";
          const hero =
            heroImage.indexOf(".png") !== -1
              ? heroImage.replace(".png", ".jpg")
              : heroImage;

          const pacItem = document.createElement("div");
          pacItem.classList.add("pac-item", "pac-custom");
          pacItem.innerHTML = `<div class="pac-item-bg" style="background-image: url('${newCloudinaryPath}/b_black,o_67,c_fit,w_120/${hero}')">
                <img src="${newCloudinaryPath}/w_120/${brand.branding.logo.dark_mode}"/>
            </div>
            <div class="pac-text">
              <div class="pac-loc-name">${markerPlaces[i].locName}</div>
              <div class="pac-address">${markerPlaces[i].address}</div>
            </div>`;
          toAppend.appendChild(pacItem);

          pacItem.addEventListener("mousedown", () => {
            handleAmplitude("click_web_map_content");
            const loc = markerPlaces[i].location;
            setCenter(markerPlaces[i].position);
            setListState(2);
            setCurBrand(loc.brand_id);
            setCurLoc(loc as unknown as Location);

            handleCurMarker(markerPlaces[i].location.marker);
            input.value = "";
            toAppend.innerHTML = "";
            setUserLocation({
              isSearch: true,
              isLoc: true,
              location: [
                markerPlaces[i].position.lat(),
                markerPlaces[i].position.lng(),
              ],
            });
          });
        }
      }
    });

    // Listen for the event fired when the user selects a prediction and retrieve
    // more details for that place.
    searchBox.addListener("places_changed", () => {
      handleAmplitude("click_web_map_content");
      const places = searchBox.getPlaces();
      if (!places || places.length === 0) {
        return;
      }
      // For each place, get the icon, name and location.
      const bounds = new google.maps.LatLngBounds();
      input.value = "";
      places.forEach((place: google.maps.Place) => {
        if (!place.geometry || !place.geometry.location) return;

        setUserLocation({
          isSearch: true,
          isLoc: false,
          location: [
            place.geometry.location.lat(),
            place.geometry.location.lng(),
          ],
        });
        const toAppend = document.getElementsByClassName("pac-container")[0];
        toAppend.innerHTML = "";

        if (place.geometry.viewport) {
          // Only geocodes have viewport.
          bounds.extend(place.geometry.viewport.getNorthEast());
          bounds.extend(place.geometry.viewport.getSouthWest());
        } else {
          bounds.extend(place.geometry.location);
        }
      });
      map.fitBounds(bounds);
      const center = map.getCenter();
      if (center) {
        setCenter(center);
      }
    });
  };

  // ─────────────────────────────────────────
  // Utility Functions
  // ─────────────────────────────────────────
  const getFilteredLocations = (locations: Location[]) => {
    return locations.filter((location) => {
      const visibilityFlags = vmDecimalToFlatShiftHash(location.vm);
      return !visibilityFlags.hide_map_new;
    });
  };

  // ─────────────────────────────────────────
  // API Data Handling
  // ─────────────────────────────────────────
  const handleSuccess = (data: LocationsAndBrandsData) => {
    const brandsList: BrandList = {};
    for (const brand of data.brands) {
      brandsList[brand.brand_id] = brand;
    }

    const filteredLocations = getFilteredLocations(
      filterLocationsByBrandAndDelivery(
        data.locations,
        brandsList,
        brandSlug,
        showDeliveryOnly,
      ),
    );
    setBrands(brandsList);
    setCurrentPage(1);
    handleLocsAndMarkers(filteredLocations, true);
    handleBuildTags(data.tags);
    handleLocationParam(data.locations, brandsList);
    // assigning filteredLocations ref to initLocations for data enrichment
    // filteredLocations in this method does not have markers and enriched data that is set in handleLocsAndMarkers
    setInitLocations(filteredLocationsRef.current);
    if (brandSlug) {
      handleBrandSelection(filteredLocations);
    }
  };

  const handleBrandSelection = (filteredLocations: Location[]) => {
    if (!map || !filteredLocations?.length) return;
    map.setOptions({ maxZoom: 16 });
    const bounds = new google.maps.LatLngBounds();
    let hasValidLocation = false;

    filteredLocations.forEach((loc) => {
      const lat = loc.location?.latitude;
      const lng = loc.location?.longitude;

      if (lat != null && lng != null) {
        bounds.extend(new google.maps.LatLng(lat, lng));
        hasValidLocation = true;
      }
    });

    // Only call fitBounds if we have at least one valid location
    if (hasValidLocation) {
      map?.fitBounds(bounds, {
        top: 50,
        left: 20,
        right: 20,
      });
    }
    google.maps.event.addListenerOnce(map, "idle", () => {
      map.setOptions({ maxZoom: 20 });
    });
  };

  /**
   * Converts a decimal number into a flat hash of visibility flags
   * Each flag represents whether to hide content in different contexts (map, search etc)
   * and for different user types (new vs repeat)
   *
   * @param decimal - The decimal number to convert to visibility flags
   * @returns Object with boolean flags like hide_map_new, hide_search_repeat etc
   */
  const vmDecimalToFlatShiftHash = (decimal: number | null | undefined) => {
    // Handle null/undefined values by defaulting to 0 (no visibility restrictions)
    const safeDecimal = decimal ?? 0;

    // Define the contexts and user types that make up the visibility flags
    const contexts = ["map", "search", "autocomplete", "carousels"] as const;
    const userTypes = ["new", "repeat"] as const;

    // Calculate total bits needed and convert decimal to padded binary
    const totalBits = contexts.length * userTypes.length;
    const binaryStr = safeDecimal.toString(2).padStart(totalBits, "0");

    const result: Record<string, boolean> = {};

    // For each context (map, search etc)
    contexts.forEach((context, contextIndex) => {
      let hasHiddenFlag = false;

      // For each user type (new, repeat)
      userTypes.forEach((userType, typeIndex) => {
        // Calculate bit position and check if flag is set
        const bitPos =
          totalBits - (contextIndex * userTypes.length + typeIndex) - 1;
        const key = `hide_${context}_${userType}`;
        const isHidden = binaryStr[bitPos] === "1";

        result[key] = isHidden;
        hasHiddenFlag ||= isHidden;
      });

      // Add aggregate flag for the context
      result[`hide_${context}`] = hasHiddenFlag;
    });

    return result;
  };

  const handleLocationParam = (
    allLocations: Location[],
    brandsList: BrandList,
  ) => {
    const locationParam = queryParams.location;
    if (!locationParam) return;
    const location = allLocations.find(
      (loc) => loc.location_id === +locationParam,
    );
    if (!location) return;
    const brandId = brandsList?.[location?.brand_id]?.brand_id;
    if (!brandId) return;
    // Only call handleBrandInfoClick if marker is defined
    if (location.marker) {
      handleBrandInfoClick(location, location.marker, brandId);
    }
  };

  // ─────────────────────────────────────────
  // Custom Hook Setup
  // ─────────────────────────────────────────
  const { setCenter, changeCenterOnResize } = useMapCentering(
    map,
    setViewCenter,
    showSearchBox,
    showSidebar,
    showZoombox,
    showPassPopup,
  );
  const { handleCurMarker, handleBrandInfoClick } = useMapHandlers({
    map,
    oval,
    setCenter,
    setListState,
    setCurLoc,
    setCurBrand,
    setCurMarker,
    handleAmplitude,
    curMarkerRef,
    setUserLocation,
    loadingDetails,
  });
  const { handleLocsAndMarkers } = useLocationManager({
    map,
    oval,
    showSearchBox,
    userLocation,
    curLoc,
    curMarker,
    markersState,
    locationsRef,
    curMarkerRef,
    listStateRef,
    setMarkersState,
    setLocations,
    setFilteredLocations,
    setCurBrand,
    setCurLoc,
    setListState,
    setInfoOpenMobile,
    handleCurMarker,
    handleAmplitude,
    setCurrentPage,
    showSidebar,
    showZoombox,
    showPassPopup,
    staticLocation,
    initSelectedTag,
    brands,
    selectedCuisines,
  });

  const { handleStart, handleSetMap } = useMapSetup({
    apiKey: "AIzaSyCHGODsYs-DA5vf_G64xrllxB9HGo9zmwg", // (use ENV var later)
    setMap,
    setIsLoaded,
    setCenter,
    widget,
    markersState,
    userLocation,
    handleAmplitude,
    setUserLocation,
    setStaticLocation,
    isHamburgerRequired,
    hamburgerStateRef,
    setHamburgerState,
  });

  const {
    handleTagsChange,
    handleInitSelected,
    handleBuildTags,
    getFeaturedTags,
  } = useTagsFilter({
    initLocations,
    brands,
    markersState,
    setCurLoc,
    setCurBrand,
    setListState,
    setMarkersState,
    setCurrentPage,
    setFilteredLocations,
    setInfoOpenMobile,
    tags,
    selectedCuisines,
    selectedMoods,
    setTags,
    handleCurMarker,
    handleLocsAndMarkers,
    queryParams,
    setInitSelectedTag,
    initSelectedTag,
    map,
  });

  const fetchLocationsAndBrands = useFetchLocationsAndBrands({
    brandSlug,
    queryParams,
    showDeliveryOnly,
    hydrateLocationsAndBrands: handleSuccess,
    markersState,
    setMarkersState,
  });

  const handleMoveCenter = debounce(() => {
    if (locations?.length > 0) {
      handleLocsAndMarkers(locations, false);
    }
  }, 600);

  return (
    <div className="outer" onClick={(e) => handleCloseMenus(e)}>
      {showSearchBox && (
        <MapHeaderContainer
          infoOpenMobile={infoOpenMobile}
          setInfoOpenMobile={setInfoOpenMobile}
          hamburgerState={hamburgerState}
          widget={widget}
          setUserLocation={setUserLocation}
          staticLocation={staticLocation}
          userLocation={userLocation}
          map={map}
          setEnteredThroughButton={setEnteredThroughButton}
          colorMode={colorMode}
          selectedCuisines={selectedCuisines}
          setSelectedCuisines={setSelectedCuisines}
          selectedMoods={selectedMoods}
          setSelectedMoods={setSelectedMoods}
          moodsIsOpen={moodsIsOpen}
          cuisinesIsOpen={cuisinesIsOpen}
          setMoodsIsOpen={setMoodsIsOpen}
          setCuisinesIsOpen={setCuisinesIsOpen}
          placeHolder={placeHolder}
          initSelectedTag={initSelectedTag}
          logo={logo}
          ikname={ikname}
          inkindStateDomain={inKindDomain}
          isMobile={isMobile}
        />
      )}
      <div
        className="map-outside"
        style={{
          display: !!isLoaded ? "block" : "none",
          borderRadius: widget === true ? "24px" : "0px",
          border: widget === true ? "2px solid #ccc" : "0px",
        }}
      >
        {showSidebar && (
          <SidebarContainer
            listState={listState}
            widget={widget}
            filteredLocations={filteredLocations}
            locations={locations}
            setFilteredLocations={setFilteredLocations}
            handleCurMarker={handleCurMarker}
            setInfoOpenMobile={setInfoOpenMobile}
            enteredThroughButton={enteredThroughButton}
            colorMode={colorMode}
            brands={brands}
            cloudinaryPath={newCloudinaryPath}
            userLocErr={userLocErr}
            handleBrandInfoClick={handleBrandInfoClick}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            curBrand={curBrand}
            curLoc={curLoc}
            tags={tags}
            getFeaturedTags={getFeaturedTags}
            curMarker={curMarker}
            tiers={tiers}
            ctaDisplay={ctaDisplay}
            purchaseModeTiers={purchaseModeTiers}
            totalLocationCount={totalLocationCount}
            showBonusBox={showBonusBox}
            setCurLoc={setCurLoc}
            setListState={setListState}
            loadingDetails={loadingDetails}
            curMarkerRef={curMarkerRef}
            oval={oval}
            counter={counter}
          />
        )}
        <AnimatePresence>
          {!!curBrand && !!curLoc && infoOpenMobile === false && (
            <MobileBrandInfo
              curLoc={curLoc}
              brands={brands}
              cloudinaryPath={newCloudinaryPath}
              userLocErr={userLocErr}
              handleBrandInfoClick={handleBrandInfoClick}
              setInfoOpenMobile={setInfoOpenMobile}
              infoOpenMobile={infoOpenMobile}
              tags={tags}
              getFeaturedTags={getFeaturedTags}
              loadingDetails={loadingDetails}
              curMarkerRef={curMarkerRef}
              oval={oval}
              counter={counter}
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!!popUpOpen &&
            !isPassPage &&
            ctaDisplay !== "none" &&
            showPassPopup && (
              <PassPopup
                infoOpenMobile={infoOpenMobile}
                curLoc={curLoc}
                curBrand={curBrand}
                popUpOpen={popUpOpen}
                setPopUpOpen={setPopUpOpen}
                purchaseModeTiers={purchaseModeTiers}
                maxBonus={
                  tiers.length
                    ? tiers[tiers.length - 1].rounded_bonus_amount
                    : 0
                }
                totalLocationCount={totalLocationCount}
              />
            )}
        </AnimatePresence>
        {!!map && !infoOpenMobile && showZoombox && (
          <ZoomBox
            map={map}
            setCenter={setCenter}
            viewCenter={viewCenter}
            isMobile={isMobile}
          />
        )}
        {isMobile &&
          (!infoOpenMobile || (listState === 1 && infoOpenMobile)) && (
            <ToggleSwitch
              infoOpenMobile={infoOpenMobile}
              setInfoOpenMobile={setInfoOpenMobile}
              setEnteredThroughButton={setEnteredThroughButton}
            />
          )}
        <MapContainer
          userLocation={userLocation}
          widget={widget}
          onMapLoad={handleMapLoad}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

export default withEventsLogger(App);
